import { SelectfiWidgetSdk } from "./SelectfiWidgetSdk";

export class ContentContainer {
  constructor(
    readonly id: string,
    readonly element: HTMLElement,
    public productType: ProductType, // Not read only to allow swapping calculator to screening
    readonly pageType: PageType,
    readonly widgetType: WidgetType,
    readonly vin?: string,
    readonly vehiclePrice?: number,
    readonly vehicleYear?: number,
    readonly vehicleMake?: string,
    readonly vehicleModel?: string,
    readonly dealerName?: string
  ) { }

  static createFromElement(element: HTMLElement, isMultiDealer: boolean): ContentContainer {
    const { dataset } = element;

    // Field required for all products
    const product = dataset.selectfiProduct;
    if (!product) {
      throw new Error('Missing data-selectfi-product attribute');
    }
    if (!Object.values(ProductType).includes(product as ProductType)) {
      throw new Error(`Invalid data-selectfi-product attribute: ${product}`);
    }

    const pageType = dataset.selectfiPage;
    if (!pageType) {
      throw new Error('Missing data-selectfi-page attribute');
    }
    if (!Object.values(PageType).includes(pageType as PageType)) {
      throw new Error(`Invalid data-selectfi-page attribute: ${pageType}`);
    }

    const widgetType = dataset.selectfiWidgetType;
    if (!widgetType) {
      throw new Error('Missing data-selectfi-widget-type attribute');
    }
    if (!Object.values(WidgetType).includes(widgetType as WidgetType)) {
      throw new Error(`Invalid data-selectfi-widget-type attribute: ${widgetType}`);
    }

    const vin = dataset.selectfiVin;

    // Multi-dealer specific fields
    let dealerName;
    if (isMultiDealer) {
      dealerName = dataset.selectfiDealerName;
      if (!dealerName) {
        throw new Error('Missing data-selectfi-dealer-name attribute for multi-dealer setup');
      }
    }

    // calculator specific fields
    let vehicleYear, vehiclePrice, vehicleMake, vehicleModel;
    if (product === ProductType.CALCULATOR) {
      if (!dataset.selectfiVehicleYear) {
        throw new Error('Missing data-selectfi-vehicle-year attribute for calculator product');
      } else {
        vehicleYear = Number(dataset.selectfiVehicleYear);
      }
      if (!dataset.selectfiVehicleMake) {
        throw new Error('Missing data-selectfi-vehicle-make attribute for calculator product');
      } else {
        vehicleMake = String(dataset.selectfiVehicleMake);
      }
      if (!dataset.selectfiVehicleModel) {
        throw new Error('Missing data-selectfi-vehicle-model attribute for calculator product');
      } else {
        vehicleModel = String(dataset.selectfiVehicleModel);
      }
      if (!dataset.selectfiVehiclePrice) {
        throw new Error('Missing data-selectfi-vehicle-price attribute for calculator product');
      } else {
        if (dataset.selectfiVehiclePrice.includes(',') || dataset.selectfiVehiclePrice.includes('$')) {
          vehiclePrice = Number(dataset.selectfiVehiclePrice.replace(/[$,]/g, ''));
        } else {
          vehiclePrice = Number(dataset.selectfiVehiclePrice);
        }      }
    }

    return new ContentContainer(
      crypto.randomUUID(),
      element,
      product as ProductType,
      pageType as PageType,
      widgetType as WidgetType,
      vin,
      vehiclePrice,
      vehicleYear,
      vehicleMake,
      vehicleModel,
      dealerName
    );
  }
}


export enum ProductType {
  SCREENING = 'screening',
  FULL = 'full',
  CALCULATOR = 'calculator'
}

export enum PageType {
  VDP = 'vdp',
  SRP = 'srp',
  FINANCE = 'finance'
}
export enum WidgetType {
  EMBED = 'embed',
  BANNER = 'banner',
  CTA = 'cta'
}

export type Options = {
  widgetKey: string;
  dealerMap?: {
    [dealerName: string]: string;
  } // Map of dealer names to dealer keys for multi-dealer setups
  testMode?: boolean; // Enables logging and prevents event firing/submission (for staging sites)
  environment?: 'development' | 'production'; // Determines which endpoints to use (localhost vs prod)
  isDebug?: boolean; // Enables Mixpanel's own verbose console logging, independent of testMode
}

export type Config = {
  tracking: {
    ga4MeasurementId: string | null;
  };
  salesTaxRate: number;
  enabled: boolean;
  styles: {
    form: {
      backgroundColor: string;
      textColor: string;
      marginTop?: string | null;
      marginBottom?: string | null;
      padding?: string | null;
      border?: string | null;
      borderRadius?: string | null;
      primaryFontSize: string;
      secondaryFontSize: string;
      hoverFontColor?: string | null;
      hoverBackgroundColor?: string | null;
      ctaCustomCss?: string | null;
      bannerCustomCss?: string | null;
      embedCustomCss?: string | null;
    };
    calculator: {
      backgroundColor: string;
      textColor: string;
      primaryColor: string;
      marginTop?: string | null;
      marginBottom?: string | null;
      padding?: string | null;
      border?: string | null;
      borderRadius?: string | null;
      primaryFontSize: string;
      secondaryFontSize: string;
      hoverFontColor?: string | null;
      hoverBackgroundColor?: string | null;
      ctaCustomCss?: string | null;
      bannerCustomCss?: string | null;
      embedCustomCss?: string | null;
    };
  };
  copy: {
    form: {
      headerText: string;
      primaryText: string;
      secondaryText: string;
    };
    calculator: {
      headerText: string;
      primaryText: string;
      secondaryText: string;
      continueButtonText: string;
    };
  };
  images: {
    headerLogoUrl: string;
    formBannerUrl: string;
    calculatorBannerUrl: string;
  };
}

declare global {
  interface Window {
    SelectFI: SelectfiWidgetSdk;
  }
}
